import React, { useState, useMemo, useRef, useEffect } from 'react';
import type { RegisterSession } from '../../types';
import type { SessionRegisterSales } from '../../lib/sessionRegisterSales';
import { useSoftKeyboard } from '../../contexts/SoftKeyboardContext';
import { printRegisterCloseSummary } from '../../lib/registerCloseSummaryPrint';

interface RegisterModalProps {
  session: RegisterSession | undefined;
  /** Precomputed in parent — avoids scanning all orders when the modal opens. */
  sessionSales: SessionRegisterSales;
  /** Signed-in user performing close / print (shown on register close slip). */
  closedByDisplayName?: string | null;
  onClose: () => void;
  onOpenRegister: (openingFloat: number) => void;
  onCloseRegister: (
    closingFloat: number,
    session: RegisterSession,
    todaysSales: { cash: number; card: number; cashDenominations?: { denom: number; count: number }[] },
  ) => void;
}

const RegisterModal: React.FC<RegisterModalProps> = ({
  session,
  sessionSales,
  closedByDisplayName,
  onClose,
  onOpenRegister,
  onCloseRegister,
}) => {
  const [openingFloat, setOpeningFloat] = useState<number | ''>('');
  const [closingFloat, setClosingFloat] = useState<number | ''>('');
  const closingFloatRef = useRef<string>('');
  const closingFloatInputRef = useRef<HTMLInputElement>(null);
  const openingFloatInputRef = useRef<HTMLInputElement>(null);
  const [balanceVerified, setBalanceVerified] = useState(false);
  const { openKeyboard, enabled: softKeyboardEnabled } = useSoftKeyboard();

  const cashDenominationsTemplate = useMemo(
    () => [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1],
    [],
  );
  const [closingCashDenoms, setClosingCashDenoms] = useState<{ denom: number; count: string }[]>(() =>
    [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1].map((d) => ({ denom: d, count: '' })),
  );

  const closingCashDenomTotal = useMemo(
    () =>
      closingCashDenoms.reduce((sum, d) => {
        const c = Number(d.count);
        if (!Number.isFinite(c) || c < 0) return sum;
        return sum + d.denom * c;
      }, 0),
    [closingCashDenoms],
  );

  const formatCurrency = (n: number) => (Number.isFinite(n) ? `$${n.toFixed(2)}` : '—');
  
  // Update ref whenever closingFloat changes
  useEffect(() => {
    closingFloatRef.current = String(closingFloat || '');
  }, [closingFloat]);
  
  // Reset closing float and denomination grid when modal opens for closing
  useEffect(() => {
    if (session) {
      setClosingFloat('');
      closingFloatRef.current = '';
      if (session.cashDenominations && session.cashDenominations.length > 0) {
        const map = new Map(session.cashDenominations.map((d) => [d.denom, String(d.count)] as const));
        setClosingCashDenoms(cashDenominationsTemplate.map((d) => ({ denom: d, count: map.get(d) ?? '' })));
      } else {
        setClosingCashDenoms(cashDenominationsTemplate.map((d) => ({ denom: d, count: '' })));
      }
    }
  }, [session, cashDenominationsTemplate]);

  // Avoid auto-opening the soft keyboard on mount (feels sluggish on close register).
  useEffect(() => {
    if (!session || softKeyboardEnabled) return;
    const id = window.setTimeout(() => {
      closingFloatInputRef.current?.focus({ preventScroll: true });
    }, 80);
    return () => clearTimeout(id);
  }, [session, softKeyboardEnabled]);
  
  const handleOpen = (e: React.FormEvent) => {
    e.preventDefault();
    if (!balanceVerified) {
      alert('Please verify the opening balance before proceeding.');
      return;
    }
    if (typeof openingFloat === 'number' && openingFloat >= 0) {
      onOpenRegister(openingFloat);
      onClose();
    }
  };
  
  const handleClose = (e: React.FormEvent) => {
    e.preventDefault();
    // Use ref value as it's always the most up-to-date
    const finalClosingFloat = closingFloatRef.current ? parseFloat(closingFloatRef.current) : closingFloat;
    if (session && typeof finalClosingFloat === 'number' && finalClosingFloat >= 0) {
      const denomsPayload = closingCashDenoms
        .map((d) => ({ denom: d.denom, count: Number(d.count) }))
        .filter((d) => !Number.isNaN(d.count) && d.count > 0);
      onCloseRegister(finalClosingFloat, session, {
        ...sessionSales,
        ...(denomsPayload.length > 0 ? { cashDenominations: denomsPayload } : {}),
      });
      onClose();
    }
  };

  const expectedCash = (session?.openingFloat || 0) + sessionSales.cash;
  // Calculate variance using ref value if available, otherwise use state
  const currentClosingFloat = closingFloatRef.current ? parseFloat(closingFloatRef.current) : closingFloat;
  const variance = typeof currentClosingFloat === 'number' && !isNaN(currentClosingFloat) 
    ? currentClosingFloat - expectedCash 
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto my-auto">
        {!session ? (
            <form onSubmit={handleOpen} className="p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Open Cash Register</h2>
                <p className="text-gray-600 mb-6">Enter the starting cash float and verify the opening balance.</p>
                
                <div className="space-y-4 mb-6">
                    <div>
                        <label htmlFor="openingFloat" className="block text-sm font-medium text-gray-700 mb-1">Opening Float ($)</label>
                        <input
                            ref={openingFloatInputRef}
                            type="number"
                            id="openingFloat"
                            value={openingFloat}
                            onChange={(e) => {
                              if (!softKeyboardEnabled) {
                                setOpeningFloat(e.target.value === '' ? '' : parseFloat(e.target.value));
                              }
                            }}
                            onFocus={() => {
                              if (!openingFloatInputRef.current) return;
                              const currentValue = openingFloat === '' ? '' : String(openingFloat);
                              openKeyboard({
                                id: 'opening-float-input',
                                type: 'numeric',
                                value: currentValue,
                                label: 'Opening Float',
                                forceOpen: true,
                                onChange: (value) => {
                                  setOpeningFloat(value === '' ? '' : parseFloat(value));
                                },
                                onSubmit: () => {
                                  openingFloatInputRef.current?.blur();
                                },
                              });
                            }}
                            onClick={() => {
                              if (!openingFloatInputRef.current) return;
                              const currentValue = openingFloat === '' ? '' : String(openingFloat);
                              openKeyboard({
                                id: 'opening-float-input',
                                type: 'numeric',
                                value: currentValue,
                                label: 'Opening Float',
                                forceOpen: true,
                                onChange: (value) => {
                                  setOpeningFloat(value === '' ? '' : parseFloat(value));
                                },
                                onSubmit: () => {
                                  openingFloatInputRef.current?.blur();
                                },
                              });
                            }}
                            readOnly={softKeyboardEnabled}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 ${softKeyboardEnabled ? 'bg-gray-100 text-gray-900' : ''}`}
                            autoFocus
                            required
                            min="0"
                            step="0.01"
                            data-soft-keyboard-handled
                        />
                        <p className="text-xs text-gray-500 mt-1">This is the cash you're starting with in the register.</p>
                    </div>

                    {typeof openingFloat === 'number' && openingFloat > 0 && (
                        <div className={`p-4 rounded-lg border-2 ${balanceVerified ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={balanceVerified}
                                    onChange={(e) => setBalanceVerified(e.target.checked)}
                                    className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                                />
                                <span className={`ml-3 font-medium ${balanceVerified ? 'text-green-800' : 'text-yellow-800'}`}>
                                    I have counted ${(typeof openingFloat === 'number' ? openingFloat : 0).toFixed(2)} in the register and it is correct
                                </span>
                            </label>
                        </div>
                    )}
                </div>

                <div className="flex justify-end space-x-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                    <button 
                        type="submit" 
                        disabled={!balanceVerified}
                        className={`px-4 py-2 rounded-md font-semibold transition-colors ${
                            balanceVerified 
                                ? 'bg-green-500 text-white hover:bg-green-600' 
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        Open Register
                    </button>
                </div>
            </form>
        ) : (
             <form onSubmit={handleClose} className="p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Close Register</h2>
                <div className="space-y-3 text-gray-700 bg-gray-50 p-4 rounded-lg mb-6">
                    <p className="flex justify-between"><span>Opening Float:</span> <span className="font-semibold">${session.openingFloat.toFixed(2)}</span></p>
                    <p className="flex justify-between"><span>Cash Sales (Session):</span> <span className="font-semibold">${sessionSales.cash.toFixed(2)}</span></p>
                    <p className="flex justify-between"><span>Card Sales (Session):</span> <span className="font-semibold">${sessionSales.card.toFixed(2)}</span></p>
                    {session && (
                      <p className="text-xs text-gray-500 mt-1">
                        Session started: {new Date(session.startTime).toLocaleString()}
                      </p>
                    )}
                    <hr/>
                    <p className="flex justify-between font-bold"><span>Expected Cash in Drawer:</span> <span>${expectedCash.toFixed(2)}</span></p>
                </div>

                <label htmlFor="closingFloat" className="block text-sm font-medium text-gray-700 mb-1">Final Cash Count ($)</label>
                 <input
                    ref={closingFloatInputRef}
                    type="number"
                    id="closingFloat"
                    value={closingFloat || closingFloatRef.current || ''}
                    onChange={(e) => {
                      if (!softKeyboardEnabled) {
                        setClosingFloat(e.target.value === '' ? '' : parseFloat(e.target.value));
                      }
                    }}
                    onFocus={() => {
                      if (closingFloatInputRef.current) {
                        const currentValue = closingFloatRef.current || String(closingFloat || '');
                        openKeyboard({
                          id: 'closing-float-input',
                          type: 'numeric',
                          value: currentValue,
                          label: 'Final Cash Count',
                          forceOpen: true,
                          onChange: (value) => {
                            // Update both state and ref immediately
                            const numValue = value === '' ? '' : parseFloat(value);
                            setClosingFloat(numValue);
                            closingFloatRef.current = value;
                          },
                          onSubmit: () => {
                            // Ensure the final value is saved
                            const finalValue = closingFloatRef.current;
                            const numValue = finalValue === '' ? '' : parseFloat(finalValue);
                            setClosingFloat(numValue);
                            if (closingFloatInputRef.current) {
                              closingFloatInputRef.current.blur();
                            }
                          },
                        });
                      }
                    }}
                    readOnly={softKeyboardEnabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 bg-gray-100 text-gray-900"
                    required
                    min="0"
                    step="0.01"
                    data-soft-keyboard-handled
                />
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Cash denominations (optional)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    {closingCashDenoms.map((d) => (
                      <label key={d.denom} className="flex items-center gap-1 text-gray-700">
                        <span className="w-14 inline-block">${d.denom.toFixed(2)}</span>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={d.count}
                          onChange={(e) =>
                            setClosingCashDenoms((prev) =>
                              prev.map((p) => (p.denom === d.denom ? { ...p, count: e.target.value } : p)),
                            )
                          }
                          className="w-14 border border-gray-300 rounded px-1 py-0.5 text-right"
                        />
                        <span className="text-gray-500">pcs</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-600">Optional breakdown to reconcile the drawer vs expected cash.</p>
                  <p className="mt-2 text-sm font-semibold text-gray-900">
                    Total from denominations: {formatCurrency(closingCashDenomTotal)}
                  </p>
                  {(() => {
                    const cf = closingFloatRef.current ? parseFloat(closingFloatRef.current) : Number(closingFloat);
                    if (!Number.isFinite(cf) || String(closingFloatRef.current || closingFloat).trim() === '') return null;
                    return (
                      <p className="text-xs text-gray-600">
                        Final count − denomination total: {formatCurrency(cf - closingCashDenomTotal)}
                      </p>
                    );
                  })()}
                </div>
                 {variance !== null && (
                    <div className={`mt-4 p-3 rounded-lg text-center ${variance === 0 ? 'bg-green-100' : 'bg-yellow-100'}`}>
                        <p className="font-bold">Variance: 
                           <span className={variance < 0 ? 'text-red-600' : 'text-green-600'}>
                               ${variance.toFixed(2)} {variance > 0 ? '(Over)' : variance < 0 ? '(Short)' : ''}
                           </span>
                        </p>
                    </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      if (!session) return;
                      const closingStr = closingFloatRef.current || (closingFloat === '' ? '' : String(closingFloat));
                      printRegisterCloseSummary({
                        session,
                        closedByName: closedByDisplayName,
                        closingFloatStr: closingStr,
                        closingCashStr: String(sessionSales.cash),
                        closingCardStr: String(sessionSales.card),
                        cashDenoms: closingCashDenoms,
                        cashDenomTotal: closingCashDenomTotal,
                      });
                    }}
                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Print summary
                  </button>
                  <div className="flex space-x-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                    <button type="submit" className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">Close Register</button>
                  </div>
                </div>
             </form>
        )}
      </div>
    </div>
  );
};

export default RegisterModal;
